import { type MediaAsset } from '@resolute/schema';
import { Download, FileText } from 'lucide-react';

/** Extension badge, read off the URL ("…/brochure.pdf" -> "PDF"). Undefined
 *  when CPzone stored the file without a recognisable extension — the badge is
 *  then simply omitted rather than guessed at. */
function fileExtension(url: string): string | undefined {
  return /\.([a-z0-9]{2,4})(?:[?#]|$)/i.exec(url)?.[1]?.toUpperCase();
}

/** The API sends the upstream filename as the label. Only the extension is
 *  stripped (it already shows as its own badge) and the first letter raised —
 *  the wording itself is left exactly as CPzone recorded it. */
function label(asset: MediaAsset, fallback: string): string {
  const name = asset.alt.replace(/\.[a-z0-9]{2,4}$/i, '').trim();
  if (!name) return fallback;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/**
 * Downloadable files for a listing — brochures, floor plans. Rendered only
 * when the project actually has one; most CPzone projects have a brochure and
 * nothing else, so this is usually a single row.
 */
export function PropertyDocuments({
  documents,
  title,
}: {
  documents: MediaAsset[];
  title: string;
}) {
  if (documents.length === 0) return null;

  return (
    <section aria-labelledby="documents-heading">
      <h2 id="documents-heading" className="text-lg font-semibold">
        Documents
      </h2>
      <ul className="mt-3 flex flex-col gap-2">
        {documents.map((doc) => {
          const ext = fileExtension(doc.url);
          return (
            <li key={doc.url}>
              {/* Opened in a new tab rather than carrying `download`: these are
                  cross-origin CPzone URLs, where the attribute is ignored by
                  the browser and would promise a save that never happens. */}
              <a
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 rounded-lg border border-border bg-card p-4 transition-colors duration-fast ease-out-expo hover:border-primary/50 hover:bg-muted/40"
              >
                <FileText className="size-5 shrink-0 text-primary" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                  {label(doc, `${title} ${doc.type === 'floor-plan' ? 'floor plan' : 'brochure'}`)}
                </span>
                {ext && (
                  <span className="shrink-0 rounded border border-border px-1.5 py-0.5 text-xs uppercase tracking-wide text-muted-foreground">
                    {ext}
                  </span>
                )}
                <Download
                  className="size-4 shrink-0 text-muted-foreground transition-colors duration-fast ease-out-expo group-hover:text-primary"
                  aria-hidden="true"
                />
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
